import { FormikErrors, FormikHelpers, FormikTouched, getIn } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import { EMAIL_REGEX, PASSWORD_REGEX } from "@/constants/CRegex";

export function genFormData<T extends object>(data: T): FormData {
	const formData = new FormData();

	Object.entries(data).forEach(([key, value]) => {
		if (value === undefined || value === null) return;

		// Cas spécial : booléens
		if (typeof value === "boolean") {
			formData.append(key, String(value));
			return;
		}

		// Cas spécial : File ou Blob
		if (value instanceof File || value instanceof Blob) {
			formData.append(key, value);
			return;
		}

		// Cas par défaut
		formData.append(key, String(value));
	});

	return formData;
}

/**
 * An email address, the app's rule (EMAIL_REGEX): a domain with an
 * extension. Yup's .email() follows the HTML spec, which takes
 * "name@domain" with no extension - an address nobody can write to.
 * Optional; chain .required() where it is. Same `t()` caveat as below.
 */
export const emailSchema = () =>
	Yup.string()
		.trim()
		.matches(EMAIL_REGEX, {
			message: t("forms.email.invalid"),
			excludeEmptyString: true,
		});

/**
 * The password rule shared by the register form and the profile's
 * "change password": 8+ characters, PASSWORD_REGEX (upper, lower, digit,
 * special). A function, not a constant: `t()` is i18next's plain export,
 * so call it after i18next.init() (inside the component / schema builder).
 */
export const passwordSchema = () =>
	Yup.string()
		.required(t("forms.password.required"))
		.min(8, t("forms.password.constraintsText"))
		.test(
			"password",
			t("forms.password.constraintsText"),
			(value) => !!value && PASSWORD_REGEX.test(value)
		);

/**
 * The error to show under a Formik field: its message once the field was
 * touched or the form submitted, nothing before. amphore-lib's v2 fields
 * no longer read Formik themselves (no ambient form awareness), so every
 * field is handed its error: `error={fieldError(formik, "email")}`.
 */
export const fieldError = <T>(
	formik: {
		errors: FormikErrors<T>;
		touched: FormikTouched<T>;
		submitCount: number;
	},
	name: string
): string | undefined => {
	const error: unknown = getIn(formik.errors, name);
	if (typeof error !== "string") return undefined;

	return getIn(formik.touched, name) || formik.submitCount > 0
		? error
		: undefined;
};

/**
 * Sets a field from its onChange and marks it touched, so its message
 * shows as it is typed. Touched first, without validating: Formik's
 * setFieldTouched validates the values it holds at that moment, which
 * after a setFieldValue are still the previous ones - the message then
 * lagged one change behind. setFieldValue validates the new value.
 */
export const touchAndSet = <T>(
	formik: Pick<FormikHelpers<T>, "setFieldTouched" | "setFieldValue">,
	name: string,
	value: unknown
) => {
	formik.setFieldTouched(name, true, false);
	return formik.setFieldValue(name, value);
};
